import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Music2,
  TrendingUp,
  ListMusic,
  Mic2,
  Disc3,
  Newspaper,
  Send,
  Instagram,
  Facebook,
  Search,
  Users,
} from "lucide-react";

// Main inline nav (shown on lg+ screens only). Keep this list small to avoid overflow.
const mainNav = [
  { to: "/ranking", label: "Top 10", icon: TrendingUp, match: (p) => p === "/ranking" },
  { to: "/playlists", label: "Playlists", icon: ListMusic, match: (p) => p.includes("/playlist") },
  { to: "/dvds", label: "DVDs", icon: Disc3, match: (p) => p.includes("/dvd") },
  {
    to: "/vitrine",
    label: "Vitrine",
    icon: Music2,
    match: (p) => p === "/vitrine" || p.startsWith("/video/"),
  },
  {
    to: "/artistas",
    label: "Artistas",
    icon: Users,
    match: (p) => p.startsWith("/artista"),
  },
  {
    to: "/categoria/Not%C3%ADcias",
    label: "Notícias",
    icon: Newspaper,
    match: (p) => decodeURIComponent(p).includes("Notícias"),
  },
];

// Categories shown only on mobile menu / additional links
const mobileExtraNav = [
  { to: "/categoria/Shows", label: "Shows", icon: Mic2 },
  { to: "/categoria/Lan%C3%A7amentos", label: "Lançamentos", icon: Disc3 },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQ.trim().length < 2) return;
    navigate(`/buscar?q=${encodeURIComponent(searchQ.trim())}`);
    setSearchOpen(false);
    setSearchQ("");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 gap-3 lg:gap-4">
          {/* Logo — fixed size, never shrinks */}
          <Link
            to="/"
            className="flex items-center shrink-0"
            data-testid="logo-link"
            translate="no"
          >
            <img
              src="/arrochaplay-logo.png?v=11"
              alt="ArrochaPlay"
              className="h-8 lg:h-9 w-auto select-none"
              draggable="false"
            />
          </Link>

          {/* Desktop Inline Navigation — only lg+, with overflow hidden as safety net */}
          <div
            className="hidden lg:flex items-center gap-1 xl:gap-2 flex-1 justify-center min-w-0 overflow-hidden"
          >
            {mainNav.map((item) => {
              const isActive = item.match(location.pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm font-medium transition-all hover:bg-white/5 ${
                    isActive
                      ? "text-[#FF007F] bg-[#FF007F]/10"
                      : "text-zinc-400 hover:text-white"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  title={item.label}
                >
                  <item.icon size={16} />
                  <span className="hidden xl:inline">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Right-side cluster — desktop only, never shrinks */}
          <div className="hidden lg:flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => setSearchOpen((s) => !s)}
              className={`p-2 rounded-full transition-colors ${
                searchOpen
                  ? "text-[#FF007F] bg-[#FF007F]/10"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
              aria-label="Buscar"
              data-testid="header-search-btn"
            >
              <Search size={18} />
            </button>
            <a
              href="https://www.instagram.com/arrochaplayon"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden xl:flex p-2 rounded-full text-zinc-400 hover:text-pink-400 hover:bg-white/5 transition-colors"
              data-testid="instagram-header-link"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              href="https://www.facebook.com/share/18buTvPHdF/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden xl:flex p-2 rounded-full text-zinc-400 hover:text-blue-400 hover:bg-white/5 transition-colors"
              data-testid="facebook-header-link"
              aria-label="Facebook"
            >
              <Facebook size={18} />
            </a>
            <Link
              to="/envie-seu-cd"
              className="hidden xl:flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-[#FF007F]/20 text-zinc-300 hover:text-[#FF007F] font-semibold rounded-full transition-all text-xs border border-white/10 hover:border-[#FF007F]/30"
              data-testid="send-cd-link"
            >
              <Send size={13} />
              Envie seu CD
            </Link>
            <Link
              to="/admin"
              className="px-4 py-1.5 bg-[#FF007F] hover:bg-[#FF3399] text-white font-bold rounded-full transition-all text-sm shadow-md shadow-[#FF007F]/20"
              data-testid="admin-link"
              translate="no"
            >
              Admin
            </Link>
          </div>

          {/* Spacer pushes hamburger to the right on mobile */}
          <div className="flex-1 lg:hidden" />

          {/* Mobile: search + hamburger */}
          <button
            onClick={() => setSearchOpen((s) => !s)}
            className="lg:hidden p-2 text-zinc-300 hover:text-white"
            aria-label="Buscar"
            data-testid="header-search-btn-mobile"
          >
            <Search size={22} />
          </button>
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Abrir menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Search bar (expansível) */}
        {searchOpen && (
          <form onSubmit={onSearchSubmit} className="px-2 pb-3">
            <div className="relative max-w-3xl mx-auto">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500"
                size={18}
              />
              <input
                autoFocus
                type="search"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Buscar artista, música, álbum, playlist..."
                data-testid="header-search-input"
                className="w-full bg-zinc-900 border border-white/10 rounded-full pl-12 pr-4 py-2.5 text-sm focus:border-[#FF007F] focus:outline-none"
              />
            </div>
          </form>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-1">
              {mainNav.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:text-[#FF007F] hover:bg-white/5 rounded-lg transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon size={18} />
                  {item.label}
                </Link>
              ))}
              <div className="h-px bg-white/10 my-2" />
              {mobileExtraNav.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-3 px-4 py-2.5 text-zinc-400 hover:text-[#FF007F] hover:bg-white/5 rounded-lg transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon size={18} />
                  {item.label}
                </Link>
              ))}
              <Link
                to="/vitrine-cds"
                className="flex items-center gap-3 px-4 py-2.5 text-zinc-400 hover:text-[#FF007F] hover:bg-white/5 rounded-lg transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Disc3 size={18} />
                Vitrine de CDs
              </Link>
              <div className="h-px bg-white/10 my-2" />
              <Link
                to="/envie-seu-cd"
                className="flex items-center gap-3 px-4 py-2.5 text-zinc-300 hover:text-[#FF007F] hover:bg-white/5 rounded-lg transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Send size={18} />
                Envie seu CD
              </Link>
              <div className="flex items-center gap-2 px-4 py-2">
                <a
                  href="https://www.instagram.com/arrochaplayon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2 bg-white/5 hover:bg-pink-500/10 text-pink-400 rounded-lg transition-all text-sm font-medium"
                >
                  <Instagram size={16} />
                  Instagram
                </a>
                <a
                  href="https://www.facebook.com/share/18buTvPHdF/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2 bg-white/5 hover:bg-blue-500/10 text-blue-400 rounded-lg transition-all text-sm font-medium"
                >
                  <Facebook size={16} />
                  Facebook
                </a>
              </div>
              <Link
                to="/admin"
                className="mx-4 mt-2 px-4 py-2.5 bg-[#FF007F] hover:bg-[#FF3399] text-white font-bold rounded-full text-center transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
